import { useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { query } from '@/lib/api/sparql-adapter'
import { SPARQL_PREFIXES } from '@/lib/ontology/namespaces'
import { useConnectionStore } from '@/stores/connectionStore'
import { ConnectionDialog } from '@/components/instances/ConnectionDialog'
import type { SparqlResults } from '@/types/ontology'
import { Play, Copy, Trash2, Loader2, AlertCircle, CheckCircle, Clock } from 'lucide-react'

const QUERY_TEMPLATES = [
  {
    name: 'All Classes',
    query: `SELECT DISTINCT ?class ?label
WHERE {
  ?class a owl:Class .
  OPTIONAL { ?class rdfs:label ?label }
}
ORDER BY ?class
LIMIT 100`,
  },
  {
    name: 'All Instances',
    query: `SELECT ?instance ?type ?label
WHERE {
  ?instance a ?type .
  ?type a owl:Class .
  OPTIONAL { ?instance rdfs:label ?label }
}
LIMIT 100`,
  },
  {
    name: 'Market Domain',
    query: `SELECT ?instance ?type ?label
WHERE {
  ?instance a ?type .
  FILTER(STRSTARTS(STR(?type), "http://www.intellispot.com/ontology/market#"))
  OPTIONAL { ?instance rdfs:label ?label }
}
LIMIT 50`,
  },
  {
    name: 'Business Domain',
    query: `SELECT ?instance ?type ?label
WHERE {
  ?instance a ?type .
  FILTER(STRSTARTS(STR(?type), "http://www.intellispot.com/ontology/business#"))
  OPTIONAL { ?instance rdfs:label ?label }
}
LIMIT 50`,
  },
  {
    name: 'Solution Domain',
    query: `SELECT ?instance ?type ?label
WHERE {
  ?instance a ?type .
  FILTER(STRSTARTS(STR(?type), "http://www.intellispot.com/ontology/solution#"))
  OPTIONAL { ?instance rdfs:label ?label }
}
LIMIT 50`,
  },
  {
    name: 'Count by Type',
    query: `SELECT ?type (COUNT(?instance) AS ?count)
WHERE {
  ?instance a ?type .
  ?type a owl:Class .
}
GROUP BY ?type
ORDER BY DESC(?count)`,
  },
]

interface QueryResult {
  data: SparqlResults | null
  error: string | null
  duration: number
}

export function QueryEditor() {
  const { isConnected } = useConnectionStore()
  const [sparqlQuery, setSparqlQuery] = useState(QUERY_TEMPLATES[0].query)
  const [isExecuting, setIsExecuting] = useState(false)
  const [result, setResult] = useState<QueryResult | null>(null)
  const [showPrefixes, setShowPrefixes] = useState(false)

  const executeQuery = useCallback(async () => {
    setIsExecuting(true)
    setResult(null)
    const startTime = Date.now()

    try {
      const data = await query(sparqlQuery, true)
      const duration = Date.now() - startTime
      setResult({ data, error: null, duration })
    } catch (err) {
      const duration = Date.now() - startTime
      setResult({
        data: null,
        error: err instanceof Error ? err.message : 'Query execution failed',
        duration,
      })
    } finally {
      setIsExecuting(false)
    }
  }, [sparqlQuery])

  const copyQuery = useCallback(() => {
    navigator.clipboard.writeText(sparqlQuery)
  }, [sparqlQuery])

  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <ConnectionDialog />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Query Input */}
      <div className="flex gap-4">
        {/* Templates */}
        <Card className="w-48 flex-shrink-0">
          <CardHeader className="py-3 px-3">
            <CardTitle className="text-sm">Templates</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 space-y-1">
            {QUERY_TEMPLATES.map((template) => (
              <Button
                key={template.name}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs"
                onClick={() => setSparqlQuery(template.query)}
              >
                {template.name}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="flex-1">
          <CardHeader className="py-3 px-4 flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm">SPARQL Query</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setShowPrefixes(!showPrefixes)}
              >
                {showPrefixes ? 'Hide' : 'Show'} Prefixes
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={copyQuery} title="Copy">
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSparqlQuery('')}
                title="Clear"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                onClick={executeQuery}
                disabled={isExecuting || !sparqlQuery.trim()}
                size="sm"
              >
                {isExecuting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Play className="w-4 h-4 mr-1" />
                )}
                Execute
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {showPrefixes && (
              <pre className="px-4 py-2 text-xs font-mono bg-background text-text-muted border-b border-border overflow-x-auto">
                {SPARQL_PREFIXES}
              </pre>
            )}
            <textarea
              value={sparqlQuery}
              onChange={(e) => setSparqlQuery(e.target.value)}
              className="w-full h-48 p-4 font-mono text-sm bg-surface border-0 resize-none focus:outline-none focus:ring-0"
              placeholder="Enter your SPARQL query..."
              spellCheck={false}
            />
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardHeader className="py-3 px-4 flex-row items-center justify-between border-b border-border">
          <CardTitle className="text-sm">Results</CardTitle>
          {result && (
            <div className="flex items-center gap-3 text-xs">
              {result.error ? (
                <Badge variant="solution" className="gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Error
                </Badge>
              ) : (
                <>
                  <Badge variant="business" className="gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {result.data?.results.bindings.length ?? 0} results
                  </Badge>
                  <span className="text-text-muted flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {result.duration}ms
                  </span>
                </>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="flex-1 p-0 overflow-auto">
          {!result && (
            <div className="h-full flex items-center justify-center text-text-muted text-sm">
              Execute a query to see results
            </div>
          )}

          {result?.error && (
            <div className="p-4 text-solution">
              <p className="font-medium mb-2">Query Error</p>
              <pre className="text-xs bg-solution/10 p-3 rounded-lg overflow-x-auto">
                {result.error}
              </pre>
            </div>
          )}

          {result?.data && (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-background sticky top-0">
                  <tr>
                    {result.data.head.vars.map((variable) => (
                      <th
                        key={variable}
                        className="text-left px-4 py-2 font-medium text-text-muted border-b border-border"
                      >
                        ?{variable}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {result.data.results.bindings.map((binding, i) => (
                    <tr key={i} className="hover:bg-background">
                      {result.data!.head.vars.map((variable) => {
                        const cell = binding[variable]
                        return (
                          <td key={variable} className="px-4 py-2 font-mono text-xs">
                            {cell ? (
                              cell.type === 'uri' ? (
                                <a
                                  href={cell.value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  {cell.value.split(/[#/]/).pop()}
                                </a>
                              ) : (
                                <span className="text-text">{cell.value}</span>
                              )
                            ) : (
                              <span className="text-text-muted italic">null</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
